import React, {useEffect, useMemo, useState} from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Dropdown,
    Form,
    Modal,
    Row,
    Spinner,
    Table
} from 'react-bootstrap';
import axios from './axiosConfig';
import './HTTPGatewayPage.css';

interface APIKeyItem {
    id: number;
    name: string;
    key_prefix?: string;
    masked_key?: string;
    status: string;
}

interface HTTPGatewayIntegration {
    id: number;
    tenant_id?: number;
    api_key_id?: number;
    name: string;
    code: string;
    description?: string;
    source_system?: string;
    metric_name: string;
    metric_value_path: string;
    timestamp_path?: string;
    label_mappings?: any;
    static_labels?: any;
    required_fields?: any;
    sample_payload?: any;
    status: string;
    created_by_user_id?: number;
    created_at: string;
    updated_at: string;
}

interface DetectedField {
    path: string;
    displayPath: string;
    key: string;
    type: 'number' | 'text' | 'boolean' | 'time' | 'object' | 'array' | 'unknown';
    sampleValue: any;
    scope: 'root' | 'array_item';
    arrayPath?: string;
    itemPath?: string;
}

interface DetectedLabel {
    labelName: string;
    path: string;
    sampleValue: string;
    scope: 'root' | 'array_item';
    enabled: boolean;
}

interface PayloadAnalysis {
    mode: 'single' | 'array';
    arrayPath?: string;
    arrayItemCount?: number;
    numericFields: DetectedField[];
    timeFields: DetectedField[];
    labelCandidates: DetectedLabel[];
    recommendedMetricPath: string;
    recommendedTimestampPath: string;
    recommendedMetricName: string;
    recommendedName: string;
    recommendedCode: string;
    recommendedSourceSystem: string;
    recommendedStaticLabels: Record<string, string>;
    summary: string;
}

interface StaticLabelRow {
    id: string;
    key: string;
    value: string;
}

interface ExampleTemplate {
    key: string;
    name: string;
    description: string;
    suggestedName: string;
    suggestedDescription: string;
    payload: Record<string, any>;
}

type WorkflowStep = 0 | 1 | 2 | 3;

const workflowSteps: Array<{
    title: string;
    description: string;
}> = [
    {
        title: 'Paste JSON',
        description: 'Paste customer payload'
    },
    {
        title: 'Confirm Fields',
        description: 'Review detected data'
    },
    {
        title: 'Basic Info',
        description: 'Name and API key'
    },
    {
        title: 'Review & Save',
        description: 'Check and save'
    }
];

const exampleTemplates: ExampleTemplate[] = [
    {
        key: 'generic_metric',
        name: 'Generic Metric',
        description: 'A simple payload with one numeric value.',
        suggestedName: 'Generic Metric',
        suggestedDescription: 'Receive one simple metric value from a customer system.',
        payload: {
            site: 'Frankfurt DC',
            device_name: 'Router-01',
            device_ip: '10.10.20.15',
            metric_type: 'cpu_usage',
            value: 82,
            unit: '%',
            event_time: '2026-05-19 10:00:00'
        }
    },
    {
        key: 'callback_rate_report',
        name: 'Callback Rate Report',
        description: 'Repeated country records with callback rate.',
        suggestedName: 'Callback Rate By Country',
        suggestedDescription: 'Receive callback rate reports and monitor one metric sample for each country.',
        payload: {
            rule_id: 'vrfymeKf',
            end_time: '2026-05-12 17:40:00',
            countries: [
                {
                    sent: 750,
                    country: 'PH',
                    callback: 546,
                    not_callback: 204,
                    callback_rate: 73
                },
                {
                    sent: 27,
                    country: 'MY',
                    callback: 23,
                    not_callback: 4,
                    callback_rate: 85
                }
            ],
            rule_name: 'lazada-orange-mix-PH-otp',
            event_type: 'callback_rate_report',
            start_time: '2026-05-12 17:20:00',
            total_sent: 777,
            total_callback: 569,
            total_not_callback: 208,
            overall_callback_rate: 73
        }
    },
    {
        key: 'interface_traffic',
        name: 'Interface Traffic',
        description: 'Repeated interface records with traffic values.',
        suggestedName: 'Interface Traffic',
        suggestedDescription: 'Receive network interface traffic data and monitor one sample for each interface.',
        payload: {
            site: 'Frankfurt DC',
            device_name: 'Router-01',
            device_ip: '10.10.20.15',
            event_time: '2026-05-19 10:00:00',
            interfaces: [
                {
                    interface_name: 'GigabitEthernet0/1',
                    traffic_mbps: 850,
                    status: 'up'
                },
                {
                    interface_name: 'GigabitEthernet0/2',
                    traffic_mbps: 420,
                    status: 'up'
                }
            ]
        }
    },
    {
        key: 'device_availability',
        name: 'Device Availability',
        description: 'Repeated device records with availability values.',
        suggestedName: 'Device Availability',
        suggestedDescription: 'Receive device availability data and monitor one sample for each device.',
        payload: {
            customer: 'Customer A',
            region: 'Europe',
            event_time: '2026-05-19 10:00:00',
            devices: [
                {
                    device_name: 'Router-01',
                    device_ip: '10.10.20.15',
                    site: 'Frankfurt DC',
                    availability: 99.8
                },
                {
                    device_name: 'Router-02',
                    device_ip: '10.10.20.16',
                    site: 'Paris DC',
                    availability: 98.6
                }
            ]
        }
    },
    {
        key: 'api_latency',
        name: 'API Latency',
        description: 'A simple payload for API latency monitoring.',
        suggestedName: 'API Latency',
        suggestedDescription: 'Receive API latency data from a customer system.',
        payload: {
            service_name: 'payment-api',
            endpoint: '/api/v1/payment',
            region: 'eu-central',
            latency_ms: 186,
            status_code: 200,
            event_time: '2026-05-19 10:00:00'
        }
    }
];

const normalizeArray = <T,>(value: any, possibleKeys: string[] = []): T[] => {
    if (Array.isArray(value)) {
        return value;
    }

    for (const key of possibleKeys) {
        if (Array.isArray(value?.[key])) {
            return value[key];
        }
    }

    if (Array.isArray(value?.data)) {
        return value.data;
    }

    if (Array.isArray(value?.items)) {
        return value.items;
    }

    return [];
};

const parseObject = (value: any): Record<string, any> => {
    if (!value) {
        return {};
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : {};
        } catch {
            return {};
        }
    }

    return {};
};

const parseArray = (value: any): string[] => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map(String);
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed.map(String) : [];
        } catch {
            return value
                .split('\n')
                .map((item) => item.trim())
                .filter(Boolean);
        }
    }

    return [];
};

const getAxiosErrorMessage = (err: any, fallback: string): string => {
    const data = err.response?.data;

    if (typeof data === 'string') {
        return data;
    }

    if (Array.isArray(data?.errors)) {
        return data.errors.join('\n');
    }

    return (
        data?.error ||
        data?.message ||
        data?.details ||
        err.message ||
        fallback
    );
};

const normalizeCode = (value: string): string => {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
};

const isPlainObject = (value: any): value is Record<string, any> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const createRowId = (): string => {
    return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
};

const toSnakeCase = (value: string): string => {
    const normalized = value
        .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
        .replace(/[^a-zA-Z0-9]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '')
        .toLowerCase();

    return normalized || 'field';
};

const toTitleCase = (value: string): string => {
    return value
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const singularize = (value: string): string => {
    const clean = toSnakeCase(value);

    if (clean.endsWith('ies')) {
        return `${clean.slice(0, -3)}y`;
    }

    if (clean.endsWith('s') && clean.length > 1) {
        return clean.slice(0, -1);
    }

    return clean;
};

const formatSampleValue = (value: any): string => {
    if (value === null || value === undefined) {
        return '';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const formatDateTime = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
};

const getValueByPath = (payload: any, path: string): { value: any; found: boolean } => {
    const cleanPath = path.trim();

    if (!cleanPath) {
        return {value: undefined, found: false};
    }

    const parts = cleanPath
        .split('.')
        .map((item) => item.trim())
        .filter(Boolean);

    let current = payload;

    for (const part of parts) {
        if (current === null || current === undefined) {
            return {value: undefined, found: false};
        }

        if (Array.isArray(current)) {
            const index = Number(part);
            if (!Number.isInteger(index) || index < 0 || index >= current.length) {
                return {value: undefined, found: false};
            }

            current = current[index];
            continue;
        }

        if (!isPlainObject(current) || !(part in current)) {
            return {value: undefined, found: false};
        }

        current = current[part];
    }

    return {value: current, found: true};
};

const splitWildcardPath = (path: string): { arrayPath: string; itemPath: string } | null => {
    const marker = '[*].';
    const index = path.indexOf(marker);

    if (index < 0) {
        return null;
    }

    const arrayPath = path.slice(0, index).trim();
    const itemPath = path.slice(index + marker.length).trim();

    if (!arrayPath || !itemPath) {
        return null;
    }

    return {arrayPath, itemPath};
};

const detectValueType = (key: string, value: any): DetectedField['type'] => {
    const lowerKey = key.toLowerCase();

    if (Array.isArray(value)) {
        return 'array';
    }

    if (isPlainObject(value)) {
        return 'object';
    }

    if (typeof value === 'number') {
        return 'number';
    }

    if (typeof value === 'boolean') {
        return 'boolean';
    }

    if (typeof value === 'string') {
        const raw = value.trim();

        if (
            lowerKey.includes('time') ||
            lowerKey.includes('timestamp') ||
            lowerKey.endsWith('_at') ||
            /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/.test(raw)
        ) {
            return 'time';
        }

        return 'text';
    }

    return 'unknown';
};

const flattenObjectFields = (
    value: Record<string, any>,
    options: {
        prefix?: string;
        scope: 'root' | 'array_item';
        arrayPath?: string;
        includeArrays?: boolean;
    }
): DetectedField[] => {
    const fields: DetectedField[] = [];
    const prefix = options.prefix || '';

    Object.entries(value).forEach(([key, fieldValue]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        const type = detectValueType(key, fieldValue);

        if (type === 'object' && isPlainObject(fieldValue)) {
            fields.push(
                ...flattenObjectFields(fieldValue, {
                    prefix: path,
                    scope: options.scope,
                    arrayPath: options.arrayPath,
                    includeArrays: options.includeArrays
                })
            );
            return;
        }

        if (type === 'array' && !options.includeArrays) {
            return;
        }

        const itemPath = options.scope === 'array_item' ? path : undefined;
        const finalPath =
            options.scope === 'array_item' && options.arrayPath
                ? `${options.arrayPath}[*].${path}`
                : path;

        fields.push({
            path: finalPath,
            displayPath: path,
            key,
            type,
            sampleValue: fieldValue,
            scope: options.scope,
            arrayPath: options.arrayPath,
            itemPath
        });
    });

    return fields;
};

const findObjectArrays = (
    value: any,
    currentPath = ''
): Array<{ path: string; items: Record<string, any>[] }> => {
    const arrays: Array<{ path: string; items: Record<string, any>[] }> = [];

    if (!isPlainObject(value)) {
        return arrays;
    }

    Object.entries(value).forEach(([key, childValue]) => {
        const childPath = currentPath ? `${currentPath}.${key}` : key;

        if (Array.isArray(childValue)) {
            const objectItems = childValue.filter(isPlainObject);

            if (objectItems.length > 0) {
                arrays.push({
                    path: childPath,
                    items: objectItems
                });
            }

            return;
        }

        if (isPlainObject(childValue)) {
            arrays.push(...findObjectArrays(childValue, childPath));
        }
    });

    return arrays;
};

const scoreMetricField = (field: DetectedField): number => {
    const key = field.key.toLowerCase();
    const path = field.displayPath.toLowerCase();

    let score = 0;

    if (key === 'value') score += 900;
    if (key.endsWith('_rate')) score += 880;
    if (key.includes('rate')) score += 840;
    if (key.includes('usage')) score += 820;
    if (key.includes('utilization')) score += 820;
    if (key.includes('availability')) score += 810;
    if (key.includes('latency')) score += 800;
    if (key.includes('rtt')) score += 790;
    if (key.includes('traffic')) score += 780;
    if (key.includes('bandwidth')) score += 770;
    if (key.includes('loss')) score += 760;
    if (key.includes('percent') || key.includes('percentage')) score += 750;
    if (key.includes('score')) score += 720;
    if (key.includes('temperature')) score += 700;
    if (key.includes('memory')) score += 690;
    if (key.includes('cpu')) score += 690;
    if (key.includes('disk')) score += 680;
    if (key.includes('count')) score += 450;
    if (key.includes('total')) score += 420;
    if (key.includes('sent')) score += 400;
    if (key.includes('received')) score += 400;
    if (key.includes('error')) score += 390;

    if (path.includes('overall')) score -= 100;
    if (field.scope === 'array_item') score += 80;

    return score;
};

const scoreTimeField = (field: DetectedField): number => {
    const key = field.key.toLowerCase();
    const path = field.displayPath.toLowerCase();

    let score = 0;

    if (key === 'event_time') score += 1000;
    if (key === 'timestamp') score += 950;
    if (key === 'end_time') score += 900;
    if (key === 'time') score += 850;
    if (key.endsWith('_at')) score += 800;
    if (key === 'start_time') score += 650;
    if (path.includes('time')) score += 300;

    return score;
};

const isGoodLabelCandidate = (field: DetectedField): boolean => {
    const key = field.key.toLowerCase();
    const value = formatSampleValue(field.sampleValue);

    if (!['text', 'boolean', 'number'].includes(field.type)) {
        return false;
    }

    if (field.type === 'number' && !key.includes('id') && !key.includes('code')) {
        return false;
    }

    if (
        key.includes('time') ||
        key.includes('timestamp') ||
        key.endsWith('_at') ||
        key.includes('description') ||
        key.includes('message') ||
        key.includes('raw') ||
        key.includes('payload') ||
        key.includes('password') ||
        key.includes('secret') ||
        key.includes('token')
    ) {
        return false;
    }

    if (value.length > 120) {
        return false;
    }

    return true;
};

const scoreLabelField = (field: DetectedField): number => {
    const key = field.key.toLowerCase();
    let score = 0;

    if (key === 'site') score += 1000;
    if (key === 'site_name') score += 990;
    if (key === 'device_ip') score += 980;
    if (key === 'device_name') score += 970;
    if (key === 'host') score += 960;
    if (key === 'hostname') score += 960;
    if (key === 'country') score += 950;
    if (key === 'region') score += 940;
    if (key === 'customer') score += 930;
    if (key === 'customer_id') score += 925;
    if (key === 'tenant') score += 920;
    if (key === 'service') score += 910;
    if (key === 'service_name') score += 910;
    if (key === 'endpoint') score += 900;
    if (key === 'interface') score += 890;
    if (key === 'interface_name') score += 890;
    if (key === 'rule_id') score += 880;
    if (key === 'rule_name') score += 870;
    if (key === 'event_type') score += 860;
    if (key === 'unit') score += 850;
    if (key === 'metric') score += 840;
    if (key === 'metric_type') score += 830;
    if (key.includes('name')) score += 700;
    if (key.includes('type')) score += 680;
    if (key.includes('id')) score += 660;
    if (key.includes('code')) score += 640;
    if (key.includes('status')) score += 620;

    if (field.scope === 'array_item') score += 80;

    return score;
};

const buildLabelCandidates = (fields: DetectedField[]): DetectedLabel[] => {
    const candidates = fields
        .filter(isGoodLabelCandidate)
        .map((field) => ({
            field,
            score: scoreLabelField(field)
        }))
        .sort((a, b) => b.score - a.score);

    const usedNames = new Set<string>();

    return candidates.map(({field}, index) => {
        let labelName = toSnakeCase(field.key);

        if (usedNames.has(labelName)) {
            labelName = toSnakeCase(field.displayPath);
        }

        usedNames.add(labelName);

        return {
            labelName,
            path: field.path,
            sampleValue: formatSampleValue(field.sampleValue),
            scope: field.scope,
            enabled: index < 10 && scoreLabelField(field) > 0
        };
    });
};

const inferSourceSystem = (payload: Record<string, any>, metricKey: string): string => {
    const possibleSource =
        payload.source_system ||
        payload.sourceSystem ||
        payload.system ||
        payload.platform ||
        payload.vendor ||
        payload.event_type ||
        payload.eventType ||
        '';

    const cleanSource = toSnakeCase(String(possibleSource));

    if (cleanSource) {
        if (cleanSource.endsWith('_report')) {
            return `${cleanSource.replace(/_report$/, '')}_monitor`;
        }

        return cleanSource;
    }

    return `${toSnakeCase(metricKey)}_monitor`;
};

const scoreLabelName = (labelName: string): number => {
    const key = labelName.toLowerCase();
    let score = 0;

    if (key === 'country') score += 1000;
    if (key === 'site') score += 950;
    if (key === 'device_ip') score += 930;
    if (key === 'device_name') score += 920;
    if (key === 'interface_name') score += 910;
    if (key === 'endpoint') score += 900;
    if (key === 'service_name') score += 890;
    if (key.includes('name')) score += 700;
    if (key.includes('id')) score += 650;

    return score;
};

const getBestArrayDimensionLabel = (labels: DetectedLabel[]): string => {
    const arrayLabels = labels
        .filter((item) => item.enabled && item.scope === 'array_item')
        .sort((a, b) => {
            const aScore = scoreLabelName(a.labelName);
            const bScore = scoreLabelName(b.labelName);
            return bScore - aScore;
        });

    return arrayLabels[0]?.labelName || '';
};

const buildMetricName = (
    metricField: DetectedField | undefined,
    labelCandidates: DetectedLabel[],
    mode: 'single' | 'array',
    arrayPath?: string
): string => {
    const metricKey = metricField ? toSnakeCase(metricField.key) : 'metric';

    if (mode === 'array') {
        const dimensionLabel = getBestArrayDimensionLabel(labelCandidates);

        if (dimensionLabel) {
            return `lynxguard_http_gateway_${metricKey}_by_${dimensionLabel}`;
        }

        if (arrayPath) {
            return `lynxguard_http_gateway_${metricKey}_by_${singularize(arrayPath.split('.').pop() || arrayPath)}`;
        }
    }

    return `lynxguard_http_gateway_${metricKey}`;
};

const analyzePayloadObject = (payload: Record<string, any>): PayloadAnalysis => {
    const rootFields = flattenObjectFields(payload, {
        scope: 'root',
        includeArrays: false
    });

    const objectArrays = findObjectArrays(payload)
        .map((arrayInfo) => {
            const firstItem = arrayInfo.items[0] || {};
            const itemFields = flattenObjectFields(firstItem, {
                scope: 'array_item',
                arrayPath: arrayInfo.path,
                includeArrays: false
            });

            const numericCount = itemFields.filter((field) => field.type === 'number').length;
            const labelCount = itemFields.filter(isGoodLabelCandidate).length;

            return {
                ...arrayInfo,
                itemFields,
                numericCount,
                labelCount
            };
        })
        .filter((arrayInfo) => arrayInfo.numericCount > 0)
        .sort((a, b) => {
            const aScore = a.numericCount * 100 + a.labelCount * 20;
            const bScore = b.numericCount * 100 + b.labelCount * 20;
            return bScore - aScore;
        });

    const selectedArray = objectArrays[0];

    const mode: 'single' | 'array' = selectedArray ? 'array' : 'single';
    const itemFields = selectedArray?.itemFields || [];
    const candidateFields = mode === 'array' ? itemFields : rootFields;

    const numericFields = candidateFields
        .filter((field) => field.type === 'number')
        .sort((a, b) => scoreMetricField(b) - scoreMetricField(a));

    const rootTimeFields = rootFields
        .filter((field) => field.type === 'time')
        .sort((a, b) => scoreTimeField(b) - scoreTimeField(a));

    const itemTimeFields = itemFields
        .filter((field) => field.type === 'time')
        .sort((a, b) => scoreTimeField(b) - scoreTimeField(a));

    const timeFields = [...rootTimeFields, ...itemTimeFields];
    const recommendedMetricField = numericFields[0];
    const recommendedTimeField = timeFields[0];

    const labelFields = mode === 'array'
        ? [...rootFields, ...itemFields]
        : rootFields;

    const labelCandidates = buildLabelCandidates(labelFields);
    const recommendedMetricName = buildMetricName(
        recommendedMetricField,
        labelCandidates,
        mode,
        selectedArray?.path
    );

    const eventTypeValue = formatSampleValue(payload.event_type || payload.eventType || payload.metric_type || payload.metric || '');
    const recommendedName = eventTypeValue
        ? toTitleCase(eventTypeValue)
        : recommendedMetricField
            ? toTitleCase(recommendedMetricField.key)
            : 'HTTP Gateway Integration';

    const recommendedStaticLabels: Record<string, string> = {};
    if (eventTypeValue) {
        recommendedStaticLabels.monitor_type = toSnakeCase(eventTypeValue);
    }

    const recommendedSourceSystem = inferSourceSystem(payload, recommendedMetricField?.key || 'metric');

    const summary = mode === 'array'
        ? `Found repeated records in "${selectedArray?.path}". Each item can become one metric sample.`
        : 'Found a single JSON object. It can become one metric sample.';

    return {
        mode,
        arrayPath: selectedArray?.path,
        arrayItemCount: selectedArray?.items.length,
        numericFields,
        timeFields,
        labelCandidates,
        recommendedMetricPath: recommendedMetricField?.path || '',
        recommendedTimestampPath: recommendedTimeField?.path || '',
        recommendedMetricName,
        recommendedName,
        recommendedCode: normalizeCode(recommendedName),
        recommendedSourceSystem,
        recommendedStaticLabels,
        summary
    };
};

const buildLabelMappingsFromDetectedLabels = (labels: DetectedLabel[]): Record<string, string> => {
    const output: Record<string, string> = {};

    labels.forEach((item) => {
        if (item.enabled && item.labelName.trim() && item.path.trim()) {
            output[item.labelName.trim()] = item.path.trim();
        }
    });

    return output;
};

const buildStaticLabelRows = (labels: Record<string, string>): StaticLabelRow[] => {
    return Object.entries(labels).map(([key, value]) => ({
        id: createRowId(),
        key,
        value: String(value)
    }));
};

const buildStaticLabelsFromRows = (rows: StaticLabelRow[]): Record<string, string> => {
    const output: Record<string, string> = {};

    rows.forEach((row) => {
        const key = row.key.trim();
        const value = row.value.trim();

        if (key) {
            output[key] = value;
        }
    });

    return output;
};

const parseLabelMappingsText = (value: string): Record<string, string> => {
    const parsed = parseObject(value);
    const output: Record<string, string> = {};

    Object.entries(parsed).forEach(([key, path]) => {
        const cleanKey = key.trim();
        const cleanPath = String(path).trim();

        if (cleanKey && cleanPath) {
            output[cleanKey] = cleanPath;
        }
    });

    return output;
};

const buildRequiredFields = (
    metricValuePath: string,
    timestampPath: string,
    labelMappings: Record<string, string>,
    requiredFieldsText: string
): string[] => {
    const manualFields = requiredFieldsText
        .split('\n')
        .map((item) => item.trim())
        .filter(Boolean);

    if (manualFields.length > 0) {
        return Array.from(new Set(manualFields));
    }

    const fields: string[] = [];

    const wildcard = splitWildcardPath(metricValuePath);
    if (wildcard) {
        fields.push(wildcard.arrayPath);
    } else if (metricValuePath.trim()) {
        fields.push(metricValuePath.trim());
    }

    if (timestampPath.trim() && !timestampPath.includes('[*]')) {
        fields.push(timestampPath.trim());
    }

    Object.values(labelMappings).forEach((path) => {
        const cleanPath = path.trim();
        if (!cleanPath || cleanPath.includes('[*]')) {
            return;
        }

        fields.push(cleanPath);
    });

    return Array.from(new Set(fields));
};

const buildPreviewRows = (
    payload: Record<string, any>,
    metricValuePath: string,
    timestampPath: string,
    labelMappings: Record<string, string>,
    limit = 8
): Record<string, any>[] => {
    const rows: Record<string, any>[] = [];
    const wildcard = splitWildcardPath(metricValuePath);

    if (wildcard) {
        const {value: arrayValue, found} = getValueByPath(payload, wildcard.arrayPath);

        if (!found || !Array.isArray(arrayValue)) {
            return [];
        }

        arrayValue.slice(0, limit).forEach((item, index) => {
            if (!isPlainObject(item)) {
                return;
            }

            const {value: metricValue} = getValueByPath(item, wildcard.itemPath);
            const row: Record<string, any> = {
                row: index + 1,
                value: metricValue
            };

            Object.entries(labelMappings).forEach(([labelName, path]) => {
                const labelWildcard = splitWildcardPath(path);

                if (labelWildcard && labelWildcard.arrayPath === wildcard.arrayPath) {
                    const {value: labelValue} = getValueByPath(item, labelWildcard.itemPath);
                    row[labelName] = labelValue;
                    return;
                }

                const {value: labelValue} = getValueByPath(payload, path);
                row[labelName] = labelValue;
            });

            if (timestampPath.trim()) {
                const timestampWildcard = splitWildcardPath(timestampPath);
                if (timestampWildcard && timestampWildcard.arrayPath === wildcard.arrayPath) {
                    const {value: timestampValue} = getValueByPath(item, timestampWildcard.itemPath);
                    row.sample_time = timestampValue;
                } else {
                    const {value: timestampValue} = getValueByPath(payload, timestampPath);
                    row.sample_time = timestampValue;
                }
            }

            rows.push(row);
        });

        return rows;
    }

    const {value: metricValue} = getValueByPath(payload, metricValuePath);
    const row: Record<string, any> = {
        row: 1,
        value: metricValue
    };

    Object.entries(labelMappings).forEach(([labelName, path]) => {
        const {value: labelValue} = getValueByPath(payload, path);
        row[labelName] = labelValue;
    });

    if (timestampPath.trim()) {
        const {value: timestampValue} = getValueByPath(payload, timestampPath);
        row.sample_time = timestampValue;
    }

    return [row];
};

const HTTPGatewayPage: React.FC = () => {
    const [apiKeys, setApiKeys] = useState<APIKeyItem[]>([]);
    const [integrations, setIntegrations] = useState<HTTPGatewayIntegration[]>([]);

    const [loading, setLoading] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [testing, setTesting] = useState<boolean>(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [togglingId, setTogglingId] = useState<number | null>(null);

    const [error, setError] = useState<string>('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [showIntegrationModal, setShowIntegrationModal] = useState<boolean>(false);
    const [editingIntegration, setEditingIntegration] = useState<HTTPGatewayIntegration | null>(null);

    const [name, setName] = useState<string>('');
    const [code, setCode] = useState<string>('');
    const [apiKeyId, setApiKeyId] = useState<string>('');
    const [description, setDescription] = useState<string>('');
    const [sourceSystem, setSourceSystem] = useState<string>('');
    const [enabled, setEnabled] = useState<boolean>(true);

    const [metricName, setMetricName] = useState<string>('');
    const [metricValuePath, setMetricValuePath] = useState<string>('');
    const [timestampPath, setTimestampPath] = useState<string>('');
    const [labelMappingsText, setLabelMappingsText] = useState<string>('{}');
    const [requiredFieldsText, setRequiredFieldsText] = useState<string>('');
    const [samplePayloadText, setSamplePayloadText] = useState<string>('');
    const [staticLabelRows, setStaticLabelRows] = useState<StaticLabelRow[]>([]);

    const [analysis, setAnalysis] = useState<PayloadAnalysis | null>(null);
    const [detectedLabels, setDetectedLabels] = useState<DetectedLabel[]>([]);
    const [showAdvancedSettings, setShowAdvancedSettings] = useState<boolean>(false);
    const [activeWorkflowStep, setActiveWorkflowStep] = useState<WorkflowStep>(0);

    const [showExampleModal, setShowExampleModal] = useState<boolean>(false);
    const [exampleIntegration, setExampleIntegration] = useState<HTTPGatewayIntegration | null>(null);

    const [showTestModal, setShowTestModal] = useState<boolean>(false);
    const [testIntegration, setTestIntegration] = useState<HTTPGatewayIntegration | null>(null);
    const [testPayloadText, setTestPayloadText] = useState<string>('');
    const [testResultText, setTestResultText] = useState<string>('');

    useEffect(() => {
        fetchInitialData();
    }, []);

    const currentLabelMappings = useMemo(() => {
        return parseLabelMappingsText(labelMappingsText);
    }, [labelMappingsText]);

    const activeApiKeys = useMemo(() => {
        return apiKeys.filter((item) => (item.status || '').toLowerCase() === 'active');
    }, [apiKeys]);

    const canEnterReviewStep = useMemo(() => {
        if (activeApiKeys.length === 0) {
            return false;
        }

        return activeApiKeys.some((item) => String(item.id) === apiKeyId);
    }, [activeApiKeys, apiKeyId]);

    const previewRows = useMemo(() => {
        try {
            const parsedPayload = samplePayloadText.trim()
                ? JSON.parse(samplePayloadText)
                : {};

            if (!isPlainObject(parsedPayload) || !metricValuePath.trim()) {
                return [];
            }

            return buildPreviewRows(
                parsedPayload,
                metricValuePath,
                timestampPath,
                currentLabelMappings
            );
        } catch {
            return [];
        }
    }, [samplePayloadText, metricValuePath, timestampPath, currentLabelMappings]);

    const previewColumns = useMemo(() => {
        const keys = new Set<string>();

        previewRows.forEach((row) => {
            Object.keys(row).forEach((key) => keys.add(key));
        });

        return Array.from(keys);
    }, [previewRows]);

    const fetchInitialData = async () => {
        setLoading(true);
        setError('');

        try {
            const [apiKeyResponse, integrationResponse] = await Promise.all([
                axios.get('/api/api-keys'),
                axios.get('/api/http-gateway/integrations'),
            ]);

            setApiKeys(
                normalizeArray<APIKeyItem>(apiKeyResponse.data, [
                    'api_keys',
                    'apiKeys',
                    'keys',
                ])
            );

            setIntegrations(
                normalizeArray<HTTPGatewayIntegration>(integrationResponse.data, [
                    'integrations',
                    'data',
                    'items',
                ])
            );
        } catch (err: any) {
            console.error('Failed to load HTTP Gateway data:', err);
            setError(getAxiosErrorMessage(err, 'Failed to load HTTP Gateway data.'));
        } finally {
            setLoading(false);
        }
    };

    const fetchIntegrations = async () => {
        try {
            const response = await axios.get('/api/http-gateway/integrations');

            setIntegrations(
                normalizeArray<HTTPGatewayIntegration>(response.data, [
                    'integrations',
                    'data',
                    'items',
                ])
            );
        } catch (err: any) {
            console.error('Failed to fetch HTTP Gateway integrations:', err);
            setError(getAxiosErrorMessage(err, 'Failed to fetch HTTP Gateway integrations.'));
        }
    };

    const resetIntegrationForm = () => {
        setEditingIntegration(null);
        setName('');
        setCode('');
        setApiKeyId('');
        setDescription('');
        setSourceSystem('');
        setEnabled(true);
        setMetricName('');
        setMetricValuePath('');
        setTimestampPath('');
        setLabelMappingsText('{}');
        setRequiredFieldsText('');
        setSamplePayloadText('');
        setStaticLabelRows([]);
        setAnalysis(null);
        setDetectedLabels([]);
        setShowAdvancedSettings(false);
        setActiveWorkflowStep(0);
        setError('');
        setSuccessMessage('');
    };

    const applyAnalysisResult = (
        nextAnalysis: PayloadAnalysis,
        options?: {
            keepBasicInfo?: boolean;
            keepAdvancedConfig?: boolean;
        }
    ) => {
        const nextLabels = nextAnalysis.labelCandidates;
        const nextLabelMappings = buildLabelMappingsFromDetectedLabels(nextLabels);

        setAnalysis(nextAnalysis);
        setDetectedLabels(nextLabels);

        if (!options?.keepAdvancedConfig) {
            setMetricValuePath(nextAnalysis.recommendedMetricPath);
            setTimestampPath(nextAnalysis.recommendedTimestampPath);
            setMetricName(nextAnalysis.recommendedMetricName);
            setLabelMappingsText(JSON.stringify(nextLabelMappings, null, 2));
            setRequiredFieldsText('');
            setStaticLabelRows(buildStaticLabelRows(nextAnalysis.recommendedStaticLabels));
        }

        if (!options?.keepBasicInfo) {
            if (!name.trim()) {
                setName(nextAnalysis.recommendedName);
            }

            if (!code.trim()) {
                setCode(nextAnalysis.recommendedCode);
            }

            if (!sourceSystem.trim()) {
                setSourceSystem(nextAnalysis.recommendedSourceSystem);
            }
        }
    };

    const analyzePayloadText = (rawText: string): PayloadAnalysis | null => {
        if (!rawText.trim()) {
            setError('Please paste one customer JSON payload first.');
            return null;
        }

        try {
            const parsedPayload = JSON.parse(rawText);

            if (!isPlainObject(parsedPayload)) {
                setError('Payload must be a JSON object.');
                return null;
            }

            return analyzePayloadObject(parsedPayload);
        } catch {
            setError('Payload must be valid JSON.');
            return null;
        }
    };

    const handleAnalyzePayload = () => {
        setError('');

        const nextAnalysis = analyzePayloadText(samplePayloadText);

        if (!nextAnalysis) {
            return;
        }

        applyAnalysisResult(nextAnalysis);
        setActiveWorkflowStep(1);
    };

    const loadExampleTemplate = (templateKey: string) => {
        const template = exampleTemplates.find((item) => item.key === templateKey);

        if (!template) {
            return;
        }

        const payloadText = JSON.stringify(template.payload, null, 2);
        const nextAnalysis = analyzePayloadObject(template.payload);

        setSamplePayloadText(payloadText);
        setName(template.suggestedName);
        setCode(normalizeCode(template.suggestedName));
        setDescription(template.suggestedDescription);
        setSourceSystem(nextAnalysis.recommendedSourceSystem);
        setEnabled(true);

        applyAnalysisResult(nextAnalysis, {
            keepBasicInfo: true
        });

        setActiveWorkflowStep(1);
    };

    const openCreateIntegrationModal = () => {
        resetIntegrationForm();
        setShowIntegrationModal(true);
    };

    const openEditIntegrationModal = (integration: HTTPGatewayIntegration) => {
        const currentLabelMappings = parseObject(integration.label_mappings);
        const requiredFields = parseArray(integration.required_fields);
        const samplePayload = parseObject(integration.sample_payload);
        const staticLabels = parseObject(integration.static_labels);
        const samplePayloadTextValue = Object.keys(samplePayload).length > 0
            ? JSON.stringify(samplePayload, null, 2)
            : '';

        setEditingIntegration(integration);
        setName(integration.name || '');
        setCode(integration.code || '');
        setApiKeyId(integration.api_key_id ? String(integration.api_key_id) : '');
        setDescription(integration.description || '');
        setSourceSystem(integration.source_system || '');
        setEnabled((integration.status || '').toLowerCase() !== 'disabled');
        setMetricName(integration.metric_name || '');
        setMetricValuePath(integration.metric_value_path || '');
        setTimestampPath(integration.timestamp_path || '');
        setLabelMappingsText(JSON.stringify(currentLabelMappings, null, 2));
        setRequiredFieldsText(requiredFields.join('\n'));
        setSamplePayloadText(samplePayloadTextValue);
        setStaticLabelRows(buildStaticLabelRows(staticLabels as Record<string, string>));
        setShowAdvancedSettings(false);
        setError('');
        setSuccessMessage('');

        if (samplePayloadTextValue) {
            try {
                const nextAnalysis = analyzePayloadObject(samplePayload);
                const existingPaths = new Set(Object.values(currentLabelMappings).map(String));

                const mergedDetectedLabels = nextAnalysis.labelCandidates.map((item) => ({
                    ...item,
                    enabled: existingPaths.has(item.path)
                }));

                setAnalysis(nextAnalysis);
                setDetectedLabels(mergedDetectedLabels);
                setActiveWorkflowStep(1);
            } catch {
                setAnalysis(null);
                setDetectedLabels([]);
                setActiveWorkflowStep(0);
            }
        } else {
            setAnalysis(null);
            setDetectedLabels([]);
            setActiveWorkflowStep(0);
        }

        setShowIntegrationModal(true);
    };

    const closeIntegrationModal = () => {
        if (saving) {
            return;
        }

        setShowIntegrationModal(false);
        resetIntegrationForm();
    };

    const generateCodeFromName = () => {
        setCode(normalizeCode(name || code));
    };

    const handleMetricFieldChange = (value: string) => {
        setMetricValuePath(value);

        const selectedField = analysis?.numericFields.find((item) => item.path === value);
        if (selectedField && analysis) {
            setMetricName(buildMetricName(selectedField, detectedLabels, analysis.mode, analysis.arrayPath));
        }
    };

    const handleLabelToggle = (path: string, checked: boolean) => {
        const updatedLabels = detectedLabels.map((item) => (
            item.path === path ? {...item, enabled: checked} : item
        ));

        setDetectedLabels(updatedLabels);
        setLabelMappingsText(JSON.stringify(buildLabelMappingsFromDetectedLabels(updatedLabels), null, 2));

        const selectedField = analysis?.numericFields.find((item) => item.path === metricValuePath);
        if (selectedField && analysis) {
            setMetricName(buildMetricName(selectedField, updatedLabels, analysis.mode, analysis.arrayPath));
        }
    };

    const addStaticLabelRow = () => {
        setStaticLabelRows((prev) => [
            ...prev,
            {
                id: createRowId(),
                key: '',
                value: ''
            }
        ]);
    };

    const updateStaticLabelRow = (id: string, field: 'key' | 'value', value: string) => {
        setStaticLabelRows((prev) => prev.map((item) => (
            item.id === id ? {...item, [field]: value} : item
        )));
    };

    const removeStaticLabelRow = (id: string) => {
        setStaticLabelRows((prev) => prev.filter((item) => item.id !== id));
    };

    const handleSaveIntegration = async (e: React.FormEvent) => {
        e.preventDefault();

        const cleanName = name.trim();
        const cleanCode = normalizeCode(code || name);
        const cleanMetricName = metricName.trim();
        const cleanMetricValuePath = metricValuePath.trim();

        if (!samplePayloadText.trim()) {
            setError('Customer JSON payload is required.');
            setActiveWorkflowStep(0);
            return;
        }

        let samplePayload: Record<string, any>;

        try {
            const parsedPayload = JSON.parse(samplePayloadText);
            if (!isPlainObject(parsedPayload)) {
                setError('Sample payload must be a JSON object.');
                setActiveWorkflowStep(0);
                return;
            }
            samplePayload = parsedPayload;
        } catch {
            setError('Sample payload must be valid JSON.');
            setActiveWorkflowStep(0);
            return;
        }

        if (!cleanMetricName || !cleanMetricValuePath) {
            const nextAnalysis = analyzePayloadObject(samplePayload);
            applyAnalysisResult(nextAnalysis);
            setActiveWorkflowStep(1);
            setError('Payload was analyzed. Please review the detected fields, then click Save again.');
            return;
        }

        if (!cleanName) {
            setError('Integration name is required.');
            setActiveWorkflowStep(2);
            return;
        }

        if (!cleanCode) {
            setError('Integration code is required.');
            setActiveWorkflowStep(2);
            return;
        }

        if (activeApiKeys.length === 0) {
            setError('No active API Key is available. Please create an API Key first.');
            setActiveWorkflowStep(2);
            return;
        }

        if (!apiKeyId || !activeApiKeys.some((item) => String(item.id) === apiKeyId)) {
            setError('Please select an active API Key.');
            setActiveWorkflowStep(2);
            return;
        }

        let labelMappings: Record<string, string>;

        try {
            labelMappings = parseLabelMappingsText(labelMappingsText);
        } catch {
            setError('Label mappings must be a valid JSON object.');
            setActiveWorkflowStep(3);
            return;
        }

        const staticLabels = buildStaticLabelsFromRows(staticLabelRows);

        const payload = {
            api_key_id: Number(apiKeyId),
            name: cleanName,
            code: cleanCode,
            description: description.trim(),
            source_system: sourceSystem.trim(),
            metric_name: cleanMetricName,
            metric_value_path: cleanMetricValuePath,
            timestamp_path: timestampPath.trim(),
            label_mappings: labelMappings,
            static_labels: staticLabels,
            required_fields: buildRequiredFields(
                cleanMetricValuePath,
                timestampPath,
                labelMappings,
                requiredFieldsText
            ),
            sample_payload: samplePayload,
            status: enabled ? 'enabled' : 'disabled',
        };

        setSaving(true);
        setError('');
        setSuccessMessage('');

        try {
            if (editingIntegration) {
                await axios.patch(`/api/http-gateway/integrations/${editingIntegration.id}`, payload);
                setSuccessMessage('Integration updated successfully.');
            } else {
                await axios.post('/api/http-gateway/integrations', payload);
                setSuccessMessage('Integration created successfully.');
            }

            setShowIntegrationModal(false);
            resetIntegrationForm();
            await fetchIntegrations();
        } catch (err: any) {
            console.error('Failed to save HTTP Gateway integration:', err);
            setError(getAxiosErrorMessage(err, 'Failed to save HTTP Gateway integration.'));
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteIntegration = async (integration: HTTPGatewayIntegration) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${integration.name}"?`
        );

        if (!confirmed) {
            return;
        }

        setDeletingId(integration.id);
        setError('');
        setSuccessMessage('');

        try {
            await axios.delete(`/api/http-gateway/integrations/${integration.id}`);
            setSuccessMessage('Integration deleted successfully.');
            await fetchIntegrations();
        } catch (err: any) {
            console.error('Failed to delete HTTP Gateway integration:', err);
            setError(getAxiosErrorMessage(err, 'Failed to delete HTTP Gateway integration.'));
        } finally {
            setDeletingId(null);
        }
    };

    const handleToggleIntegration = async (integration: HTTPGatewayIntegration) => {
        const isEnabled = (integration.status || '').toLowerCase() === 'enabled';
        const action = isEnabled ? 'disable' : 'enable';

        setTogglingId(integration.id);
        setError('');
        setSuccessMessage('');

        try {
            await axios.patch(`/api/http-gateway/integrations/${integration.id}/${action}`);
            setSuccessMessage(`Integration ${action}d successfully.`);
            await fetchIntegrations();
        } catch (err: any) {
            console.error('Failed to update HTTP Gateway integration status:', err);
            setError(getAxiosErrorMessage(err, 'Failed to update HTTP Gateway integration status.'));
        } finally {
            setTogglingId(null);
        }
    };

    const openExampleModal = (integration: HTTPGatewayIntegration) => {
        setExampleIntegration(integration);
        setShowExampleModal(true);
    };

    const closeExampleModal = () => {
        setShowExampleModal(false);
        setExampleIntegration(null);
    };

    const openTestModal = (integration: HTTPGatewayIntegration) => {
        const samplePayload = parseObject(integration.sample_payload);
        setTestIntegration(integration);
        setTestPayloadText(JSON.stringify(samplePayload, null, 2));
        setTestResultText('');
        setError('');
        setShowTestModal(true);
    };

    const closeTestModal = () => {
        if (testing) {
            return;
        }

        setShowTestModal(false);
        setTestIntegration(null);
        setTestPayloadText('');
        setTestResultText('');
    };

    const handleTestPayload = async () => {
        if (!testIntegration) {
            return;
        }

        let payload: Record<string, any>;

        try {
            const parsedPayload = JSON.parse(testPayloadText);
            if (!isPlainObject(parsedPayload)) {
                setError('Test payload must be a JSON object.');
                return;
            }
            payload = parsedPayload;
        } catch {
            setError('Test payload must be valid JSON.');
            return;
        }

        setTesting(true);
        setError('');
        setTestResultText('');

        try {
            const response = await axios.post(
                `/api/http-gateway/integrations/${testIntegration.id}/test`,
                {payload}
            );

            setTestResultText(JSON.stringify(response.data, null, 2));
        } catch (err: any) {
            console.error('Failed to test HTTP Gateway payload:', err);

            const data = err.response?.data;
            if (data) {
                setTestResultText(JSON.stringify(data, null, 2));
            }

            setError(getAxiosErrorMessage(err, 'Failed to test HTTP Gateway payload.'));
        } finally {
            setTesting(false);
        }
    };

    const getAPIKeyName = (apiKeyId?: number): string => {
        if (!apiKeyId) {
            return '-';
        }

        const found = apiKeys.find((item) => item.id === apiKeyId);
        return found?.name || `API Key #${apiKeyId}`;
    };

    const getStatusBadge = (status?: string) => {
        const normalizedStatus = (status || '').toLowerCase();

        if (normalizedStatus === 'enabled' || normalizedStatus === 'active') {
            return <Badge bg="success">Enabled</Badge>;
        }

        if (normalizedStatus === 'disabled') {
            return <Badge bg="secondary">Disabled</Badge>;
        }

        return <Badge bg="light" text="dark">{status || '-'}</Badge>;
    };

    const exampleCurl = useMemo(() => {
        if (!exampleIntegration) {
            return '';
        }

        const endpoint = `${window.location.origin}/api/http-gateway/events/${exampleIntegration.code}`;
        const payload = JSON.stringify(parseObject(exampleIntegration.sample_payload), null, 2);

        return `curl -X POST "${endpoint}" \\
  -H "Authorization: Bearer <YOUR_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '${payload}'`;
    }, [exampleIntegration]);

    const goToPreviousStep = () => {
        setActiveWorkflowStep((prev) => Math.max(0, prev - 1) as WorkflowStep);
    };

    const goToNextStep = () => {
        if (activeWorkflowStep === 0) {
            handleAnalyzePayload();
            return;
        }

        if (activeWorkflowStep === 1 && !analysis) {
            setError('Please analyze the customer JSON payload first.');
            return;
        }

        if (activeWorkflowStep === 2) {
            if (activeApiKeys.length === 0) {
                setError('No active API Key is available. Please create an API Key first.');
                return;
            }

            if (!apiKeyId || !activeApiKeys.some((item) => String(item.id) === apiKeyId)) {
                setError('Please select an active API Key before continuing.');
                return;
            }
        }

        setActiveWorkflowStep((prev) => Math.min(3, prev + 1) as WorkflowStep);
    };

    const renderWorkflowStepper = () => {
        return (
            <div className="http-gateway-workflow">
                {workflowSteps.map((step, index) => {
                    const isActive = activeWorkflowStep === index;
                    const isDone = activeWorkflowStep > index;
                    const isLocked =
                        (index > 0 && !analysis) ||
                        (index === 3 && !canEnterReviewStep);

                    return (
                        <button
                            key={step.title}
                            type="button"
                            className={[
                                'http-gateway-workflow-step',
                                isActive ? 'active' : '',
                                isDone ? 'done' : '',
                                isLocked ? 'locked' : ''
                            ].filter(Boolean).join(' ')}
                            disabled={isLocked || saving}
                            onClick={() => setActiveWorkflowStep(index as WorkflowStep)}
                        >
                            <span className="http-gateway-workflow-index">
                                {isDone ? '✓' : index + 1}
                            </span>

                            <span>
                                <strong>{step.title}</strong>
                                <small>{step.description}</small>
                            </span>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="http-gateway-page">
            <div className="http-gateway-header">
                <div>
                    <h2>HTTP Gateway</h2>
                    <p className="http-gateway-subtitle">
                        Let customers send JSON payloads to Lynxguard. The platform detects useful fields and creates monitoring samples automatically.
                    </p>
                </div>

                <Button variant="primary" onClick={openCreateIntegrationModal}>
                    Create Integration
                </Button>
            </div>

            {error && !showIntegrationModal && !showTestModal && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            {successMessage && (
                <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
                    {successMessage}
                </Alert>
            )}

            <Card className="http-gateway-card">
                <Card.Body>
                    {loading ? (
                        <div className="http-gateway-loading">
                            <Spinner animation="border" size="sm"/>
                            <span>Loading HTTP Gateway integrations...</span>
                        </div>
                    ) : (
                        <div className="http-gateway-table-wrapper">
                            <Table responsive hover className="http-gateway-table">
                                <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Code</th>
                                    <th>Metric</th>
                                    <th>API Key</th>
                                    <th>Status</th>
                                    <th>Description</th>
                                    <th>Created At</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>

                                <tbody>
                                {integrations.length === 0 ? (
                                    <tr>
                                        <td colSpan={8} className="http-gateway-empty">
                                            No HTTP Gateway integrations found.
                                        </td>
                                    </tr>
                                ) : (
                                    integrations.map((item) => (
                                        <tr key={item.id}>
                                            <td>
                                                <strong>{item.name}</strong>
                                            </td>
                                            <td>
                                                <code>{item.code}</code>
                                            </td>
                                            <td>
                                                <code>{item.metric_name}</code>
                                            </td>
                                            <td>{getAPIKeyName(item.api_key_id)}</td>
                                            <td>{getStatusBadge(item.status)}</td>
                                            <td className="http-gateway-description">
                                                {item.description || '-'}
                                            </td>
                                            <td>{formatDateTime(item.created_at)}</td>
                                            <td>
                                                <div className="http-gateway-actions">
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        onClick={() => openExampleModal(item)}
                                                    >
                                                        Example
                                                    </Button>

                                                    <Button
                                                        variant="outline-success"
                                                        size="sm"
                                                        onClick={() => openTestModal(item)}
                                                    >
                                                        Test
                                                    </Button>

                                                    <Button
                                                        variant="outline-warning"
                                                        size="sm"
                                                        disabled={togglingId === item.id}
                                                        onClick={() => handleToggleIntegration(item)}
                                                    >
                                                        {(item.status || '').toLowerCase() === 'enabled' ? 'Disable' : 'Enable'}
                                                    </Button>

                                                    <Button
                                                        variant="outline-light"
                                                        size="sm"
                                                        onClick={() => openEditIntegrationModal(item)}
                                                    >
                                                        Edit
                                                    </Button>

                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        disabled={deletingId === item.id}
                                                        onClick={() => handleDeleteIntegration(item)}
                                                    >
                                                        {deletingId === item.id ? 'Deleting...' : 'Delete'}
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal
                show={showIntegrationModal}
                onHide={closeIntegrationModal}
                scrollable
                size="xl"
                backdrop="static"
                keyboard={!saving}
                className="http-gateway-modal http-gateway-config-modal"
            >
                <Form onSubmit={handleSaveIntegration}>
                    <Modal.Header closeButton={!saving}>
                        <Modal.Title>
                            {editingIntegration ? 'Edit Integration' : 'Create Integration'}
                        </Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" onClose={() => setError('')} dismissible>
                                {error}
                            </Alert>
                        )}

                        <Alert variant="info" className="mb-4">
                            Start by pasting one example JSON payload from the customer. Lynxguard will detect repeated records, metric values, labels, and time fields automatically.
                        </Alert>

                        {renderWorkflowStepper()}

                        {activeWorkflowStep === 0 && (
                            <Card className="http-gateway-inner-card mb-4">
                                <Card.Body>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <div className="http-gateway-section-title mb-0">
                                            Step 1: Paste Customer JSON
                                        </div>

                                        <Dropdown>
                                            <Dropdown.Toggle
                                                variant="outline-info"
                                                size="sm"
                                                id="load-example-template-dropdown"
                                                disabled={saving}
                                            >
                                                Load Example
                                            </Dropdown.Toggle>

                                            <Dropdown.Menu>
                                                {exampleTemplates.map((template) => (
                                                    <Dropdown.Item
                                                        key={template.key}
                                                        onClick={() => loadExampleTemplate(template.key)}
                                                    >
                                                        <div>
                                                            <strong>{template.name}</strong>
                                                        </div>
                                                        <small>{template.description}</small>
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                    </div>

                                    <p className="http-gateway-help-text">
                                        Paste the JSON that the customer will send. Do not worry about Prometheus or JSON paths.
                                    </p>

                                    <Form.Group className="mb-3">
                                        <Form.Control
                                            as="textarea"
                                            rows={14}
                                            value={samplePayloadText}
                                            onChange={(e) => setSamplePayloadText(e.target.value)}
                                            placeholder='{"site":"Frankfurt DC","device_ip":"10.10.20.15","value":82,"event_time":"2026-05-19 10:00:00"}'
                                            disabled={saving}
                                        />
                                    </Form.Group>

                                    <Button
                                        variant="primary"
                                        type="button"
                                        onClick={handleAnalyzePayload}
                                        disabled={saving || !samplePayloadText.trim()}
                                    >
                                        Analyze Payload
                                    </Button>
                                </Card.Body>
                            </Card>
                        )}

                        {activeWorkflowStep === 1 && analysis && (
                            <Card className="http-gateway-inner-card mb-4">
                                <Card.Body>
                                    <div className="http-gateway-section-title">
                                        Step 2: Confirm Detected Data
                                    </div>

                                    <Alert variant="success">
                                        {analysis.summary}
                                        {analysis.mode === 'array' && (
                                            <>
                                                <br/>
                                                Detected array: <strong>{analysis.arrayPath}</strong>, records: <strong>{analysis.arrayItemCount}</strong>
                                            </>
                                        )}
                                    </Alert>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>What value do you want to monitor?</Form.Label>
                                                <Form.Select
                                                    value={metricValuePath}
                                                    onChange={(e) => handleMetricFieldChange(e.target.value)}
                                                    disabled={saving}
                                                >
                                                    {analysis.numericFields.length === 0 && (
                                                        <option value="">No numeric field detected</option>
                                                    )}

                                                    {analysis.numericFields.map((field) => (
                                                        <option key={field.path} value={field.path}>
                                                            {field.displayPath} = {formatSampleValue(field.sampleValue)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    Lynxguard recommends the most likely metric value automatically.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>When did this data happen?</Form.Label>
                                                <Form.Select
                                                    value={timestampPath}
                                                    onChange={(e) => setTimestampPath(e.target.value)}
                                                    disabled={saving}
                                                >
                                                    <option value="">Use server receive time</option>
                                                    {analysis.timeFields.map((field) => (
                                                        <option key={field.path} value={field.path}>
                                                            {field.displayPath} = {formatSampleValue(field.sampleValue)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Text className="text-muted">
                                                    If no timestamp is selected, Lynxguard will use the receive time.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <div className="http-gateway-section-title mt-3">
                                        Which fields identify this data?
                                    </div>

                                    <p className="http-gateway-help-text">
                                        These fields become labels, such as country, rule, site, device, customer, service, interface, or unit.
                                    </p>

                                    <div className="http-gateway-table-wrapper mb-3">
                                        <Table responsive hover size="sm" className="http-gateway-table">
                                            <thead>
                                            <tr>
                                                <th>Use</th>
                                                <th>Label</th>
                                                <th>Detected Field</th>
                                                <th>Example Value</th>
                                                <th>Scope</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {detectedLabels.length === 0 ? (
                                                <tr>
                                                    <td colSpan={5} className="http-gateway-empty">
                                                        No label fields detected.
                                                    </td>
                                                </tr>
                                            ) : (
                                                detectedLabels.map((item) => (
                                                    <tr key={item.path}>
                                                        <td>
                                                            <Form.Check
                                                                type="checkbox"
                                                                checked={item.enabled}
                                                                onChange={(e) => handleLabelToggle(item.path, e.target.checked)}
                                                                disabled={saving}
                                                            />
                                                        </td>
                                                        <td>
                                                            <code>{item.labelName}</code>
                                                        </td>
                                                        <td>
                                                            <code>{item.path}</code>
                                                        </td>
                                                        <td>{item.sampleValue}</td>
                                                        <td>
                                                            <Badge bg={item.scope === 'array_item' ? 'info' : 'secondary'}>
                                                                {item.scope === 'array_item' ? 'Repeated record' : 'Root'}
                                                            </Badge>
                                                        </td>
                                                    </tr>
                                                ))
                                            )}
                                            </tbody>
                                        </Table>
                                    </div>

                                    <div className="http-gateway-section-title mt-4">
                                        Preview
                                    </div>

                                    <p className="http-gateway-help-text">
                                        This is what Lynxguard will create from the customer JSON.
                                    </p>

                                    <div className="http-gateway-table-wrapper">
                                        <Table responsive hover size="sm" className="http-gateway-table">
                                            <thead>
                                            <tr>
                                                {previewColumns.length === 0 ? (
                                                    <th>Preview</th>
                                                ) : (
                                                    previewColumns.map((column) => (
                                                        <th key={column}>{column}</th>
                                                    ))
                                                )}
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {previewRows.length === 0 ? (
                                                <tr>
                                                    <td colSpan={Math.max(previewColumns.length, 1)} className="http-gateway-empty">
                                                        No preview available. Please check the detected metric value.
                                                    </td>
                                                </tr>
                                            ) : (
                                                previewRows.map((row, rowIndex) => (
                                                    <tr key={rowIndex}>
                                                        {previewColumns.map((column) => (
                                                            <td key={column}>
                                                                {formatSampleValue(row[column])}
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))
                                            )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Card.Body>
                            </Card>
                        )}

                        {activeWorkflowStep === 2 && (
                            <Card className="http-gateway-inner-card mb-4">
                                <Card.Body>
                                    <div className="http-gateway-section-title">
                                        Step 3: Basic Information
                                    </div>

                                    <p className="http-gateway-help-text">
                                        These fields are generated from the JSON automatically. You can adjust them if needed.
                                    </p>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Integration Name</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={name}
                                                    onChange={(e) => setName(e.target.value)}
                                                    placeholder="Device Availability"
                                                    disabled={saving}
                                                    required
                                                />
                                                <Form.Text className="text-muted">
                                                    Use a business name that users can understand.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>API Key</Form.Label>
                                                <Form.Select
                                                    value={apiKeyId}
                                                    onChange={(e) => setApiKeyId(e.target.value)}
                                                    disabled={saving || activeApiKeys.length === 0}
                                                    required
                                                >
                                                    {activeApiKeys.length === 0 ? (
                                                        <option value="">No active API Key available</option>
                                                    ) : (
                                                        <>
                                                            <option value="">Select API Key</option>
                                                            {activeApiKeys.map((item) => (
                                                                <option key={item.id} value={item.id}>
                                                                    {item.name} ({item.masked_key || item.key_prefix || `#${item.id}`})
                                                                </option>
                                                            ))}
                                                        </>
                                                    )}
                                                </Form.Select>

                                                {activeApiKeys.length === 0 ? (
                                                    <Alert variant="warning" className="mt-3 mb-0">
                                                        No active API Key is available. Please create an API Key first, then come back and select it.
                                                        <div className="mt-2">
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                href="/alerts/api-keys"
                                                                disabled={saving}
                                                            >
                                                                Create API Key
                                                            </Button>
                                                        </div>
                                                    </Alert>
                                                ) : (
                                                    <Form.Text className="text-muted">
                                                        The customer will use this API key to send data.
                                                    </Form.Text>
                                                )}
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Source System</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={sourceSystem}
                                                    onChange={(e) => setSourceSystem(e.target.value)}
                                                    placeholder="customer_monitoring_system"
                                                    disabled={saving}
                                                />
                                                <Form.Text className="text-muted">
                                                    Where does this data come from?
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Status</Form.Label>
                                                <div>
                                                    <Form.Check
                                                        type="switch"
                                                        id="integration-enabled-switch"
                                                        label="Enable Integration"
                                                        checked={enabled}
                                                        onChange={(e) => setEnabled(e.target.checked)}
                                                        disabled={saving}
                                                    />
                                                </div>
                                                <Form.Text className="text-muted">
                                                    Disabled integrations will not receive customer data.
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <Form.Group className="mb-0">
                                        <Form.Label>Description</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={2}
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            placeholder="Describe what this integration receives."
                                            disabled={saving}
                                        />
                                    </Form.Group>
                                </Card.Body>
                            </Card>
                        )}

                        {activeWorkflowStep === 3 && (
                            <>
                                <Card className="http-gateway-inner-card mb-4">
                                    <Card.Body>
                                        <div className="http-gateway-section-title">
                                            Step 4: Review & Save
                                        </div>

                                        <p className="http-gateway-help-text">
                                            Review the generated integration settings before saving.
                                        </p>

                                        <div className="http-gateway-example-block">
                                            <pre>{JSON.stringify({
                                                name,
                                                code: normalizeCode(code || name),
                                                source_system: sourceSystem,
                                                metric_name: metricName,
                                                metric_value_path: metricValuePath,
                                                timestamp_path: timestampPath || 'server_receive_time',
                                                label_mappings: currentLabelMappings,
                                                static_labels: buildStaticLabelsFromRows(staticLabelRows),
                                                status: enabled ? 'enabled' : 'disabled'
                                            }, null, 2)}</pre>
                                        </div>
                                    </Card.Body>
                                </Card>

                                <Card className="http-gateway-inner-card mb-4">
                                    <Card.Body>
                                        <div className="d-flex justify-content-between align-items-center">
                                            <div>
                                                <div className="http-gateway-section-title mb-1">
                                                    Advanced Settings
                                                </div>
                                                <p className="http-gateway-help-text mb-0">
                                                    Only edit these fields if the automatic detection is not correct.
                                                </p>
                                            </div>

                                            <Form.Check
                                                type="switch"
                                                id="show-advanced-settings-switch"
                                                label="Show advanced"
                                                checked={showAdvancedSettings}
                                                onChange={(e) => setShowAdvancedSettings(e.target.checked)}
                                                disabled={saving}
                                            />
                                        </div>

                                        {showAdvancedSettings && (
                                            <div className="mt-4">
                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Integration Code</Form.Label>
                                                            <div className="d-flex gap-2">
                                                                <Form.Control
                                                                    type="text"
                                                                    value={code}
                                                                    onChange={(e) => setCode(e.target.value)}
                                                                    placeholder="device-availability"
                                                                    disabled={saving}
                                                                    required
                                                                />
                                                                <Button
                                                                    variant="outline-light"
                                                                    type="button"
                                                                    onClick={generateCodeFromName}
                                                                    disabled={saving}
                                                                >
                                                                    Generate
                                                                </Button>
                                                            </div>
                                                            <Form.Text className="text-muted">
                                                                Used in request path: /api/http-gateway/events/:integrationCode
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Metric Name</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={metricName}
                                                                onChange={(e) => setMetricName(e.target.value)}
                                                                placeholder="lynxguard_http_gateway_device_availability"
                                                                disabled={saving}
                                                                required
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Row>
                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Metric Value Path</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={metricValuePath}
                                                                onChange={(e) => setMetricValuePath(e.target.value)}
                                                                placeholder="devices[*].availability"
                                                                disabled={saving}
                                                                required
                                                            />
                                                            <Form.Text className="text-muted">
                                                                Use [*] when one request contains repeated records.
                                                            </Form.Text>
                                                        </Form.Group>
                                                    </Col>

                                                    <Col md={6}>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Timestamp Path</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={timestampPath}
                                                                onChange={(e) => setTimestampPath(e.target.value)}
                                                                placeholder="event_time"
                                                                disabled={saving}
                                                            />
                                                        </Form.Group>
                                                    </Col>
                                                </Row>

                                                <Form.Group className="mb-3">
                                                    <Form.Label>Label Mappings JSON</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={7}
                                                        value={labelMappingsText}
                                                        onChange={(e) => setLabelMappingsText(e.target.value)}
                                                        placeholder='{"device_ip":"devices[*].device_ip","site":"devices[*].site"}'
                                                        disabled={saving}
                                                    />
                                                </Form.Group>

                                                <div className="http-gateway-section-title mt-4">
                                                    Static Labels
                                                </div>

                                                <p className="http-gateway-help-text">
                                                    Static labels are added to every generated sample.
                                                </p>

                                                {staticLabelRows.map((row) => (
                                                    <Row key={row.id} className="mb-2">
                                                        <Col md={5}>
                                                            <Form.Control
                                                                type="text"
                                                                value={row.key}
                                                                onChange={(e) => updateStaticLabelRow(row.id, 'key', e.target.value)}
                                                                placeholder="monitor_type"
                                                                disabled={saving}
                                                            />
                                                        </Col>
                                                        <Col md={5}>
                                                            <Form.Control
                                                                type="text"
                                                                value={row.value}
                                                                onChange={(e) => updateStaticLabelRow(row.id, 'value', e.target.value)}
                                                                placeholder="availability"
                                                                disabled={saving}
                                                            />
                                                        </Col>
                                                        <Col md={2}>
                                                            <Button
                                                                variant="outline-danger"
                                                                type="button"
                                                                onClick={() => removeStaticLabelRow(row.id)}
                                                                disabled={saving}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </Col>
                                                    </Row>
                                                ))}

                                                <Button
                                                    variant="outline-light"
                                                    type="button"
                                                    size="sm"
                                                    onClick={addStaticLabelRow}
                                                    disabled={saving}
                                                    className="mb-3"
                                                >
                                                    + Add Label
                                                </Button>

                                                <Form.Group className="mb-3">
                                                    <Form.Label>Required Fields</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={5}
                                                        value={requiredFieldsText}
                                                        onChange={(e) => setRequiredFieldsText(e.target.value)}
                                                        placeholder={'event_time\ndevices'}
                                                        disabled={saving}
                                                    />
                                                    <Form.Text className="text-muted">
                                                        One JSON path per line. Leave empty to auto-generate.
                                                    </Form.Text>
                                                </Form.Group>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </>
                        )}
                    </Modal.Body>

                    <Modal.Footer>
                        <Button variant="secondary" onClick={closeIntegrationModal} disabled={saving}>
                            Cancel
                        </Button>

                        {activeWorkflowStep > 0 && (
                            <Button
                                variant="outline-light"
                                type="button"
                                disabled={saving}
                                onClick={goToPreviousStep}
                            >
                                Back
                            </Button>
                        )}

                        {activeWorkflowStep < 3 && (
                            <Button
                                variant="primary"
                                type="button"
                                disabled={
                                    saving ||
                                    (activeWorkflowStep === 0 && !samplePayloadText.trim()) ||
                                    (activeWorkflowStep === 2 && !canEnterReviewStep)
                                }
                                onClick={goToNextStep}
                            >
                                {activeWorkflowStep === 0 ? 'Analyze Payload' : 'Next'}
                            </Button>
                        )}

                        {activeWorkflowStep === 3 && (
                            <Button variant="primary" type="submit" disabled={saving}>
                                {saving ? 'Saving...' : 'Save Integration'}
                            </Button>
                        )}
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal
                show={showExampleModal}
                onHide={closeExampleModal}
                scrollable
                size="lg"
                backdrop="static"
                className="http-gateway-modal http-gateway-example-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Request Example</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Alert variant="info">
                        Customers send JSON payloads to the integration endpoint. HTTP Gateway stores the latest metric
                        samples and exposes them through /api/http-gateway/metrics for Prometheus scraping.
                    </Alert>

                    <div className="http-gateway-section-title">
                        Endpoint
                    </div>

                    <div className="http-gateway-example-block">
                        <pre>{exampleIntegration ? `/api/http-gateway/events/${exampleIntegration.code}` : ''}</pre>
                    </div>

                    <div className="http-gateway-section-title mt-4">
                        cURL
                    </div>

                    <div className="http-gateway-example-block">
                        <pre>{exampleCurl}</pre>
                    </div>

                    <div className="http-gateway-section-title mt-4">
                        Example Payload
                    </div>

                    <div className="http-gateway-example-block">
                        <pre>{JSON.stringify(parseObject(exampleIntegration?.sample_payload), null, 2)}</pre>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={closeExampleModal}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showTestModal}
                onHide={closeTestModal}
                scrollable
                size="lg"
                backdrop="static"
                keyboard={!testing}
                className="http-gateway-modal http-gateway-example-modal"
            >
                <Modal.Header closeButton={!testing}>
                    <Modal.Title>
                        Test Payload {testIntegration ? `- ${testIntegration.name}` : ''}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {error && (
                        <Alert variant="danger" onClose={() => setError('')} dismissible>
                            {error}
                        </Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Payload JSON</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={10}
                            value={testPayloadText}
                            onChange={(e) => setTestPayloadText(e.target.value)}
                            disabled={testing}
                        />
                    </Form.Group>

                    <Button variant="primary" onClick={handleTestPayload} disabled={testing}>
                        {testing ? 'Testing...' : 'Test Payload'}
                    </Button>

                    {testResultText && (
                        <>
                            <div className="http-gateway-section-title mt-4">
                                Test Result
                            </div>

                            <div className="http-gateway-example-block">
                                <pre>{testResultText}</pre>
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer>
                    <Button variant="secondary" onClick={closeTestModal} disabled={testing}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default HTTPGatewayPage;